import React, { useState, useEffect, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider, useAuth } from './context/AuthContext';

// Keep Header/Footer/Context eager as they are always needed
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { EditModeBanner } from './components/EditModeBanner';
import { AnimatePresence, motion } from 'framer-motion';
import Lenis from 'lenis';
import { CursorProvider } from './context/CursorContext';
import { CustomCursor } from './components/CustomCursor';
import { SiteContent } from './data/content';

// Lazy load pages to split code bundles
const HomePage = lazy(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage').then(module => ({ default: module.PortfolioPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(module => ({ default: module.ServicesPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(module => ({ default: module.BlogPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then(module => ({ default: module.BlogPostPage })));
const SuccessPage = lazy(() => import('./pages/SuccessPage').then(module => ({ default: module.SuccessPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(module => ({ default: module.AdminPage })));

// Simple loading component for Suspense fallback
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '60vh', 
    width: '100%',
    color: 'var(--accent-color)' 
  }}>
    <div className="spinner" style={{ 
      width: '40px', 
      height: '40px', 
      border: '3px solid rgba(255,255,255,0.1)', 
      borderTopColor: 'currentColor', 
      borderRadius: '50%', 
      animation: 'spin 1s linear infinite' 
    }}></div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

import { BlogPost } from './pages/BlogPage';

interface LocationConfig {
  city: string;
  countryCode: string;
}

const App = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [activePage, setActivePage] = useState('home');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  
  const [liveContent, setLiveContent] = useState<SiteContent | null>(null);
  const [editableContent, setEditableContent] = useState<SiteContent | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  useEffect(() => {
    const checkForTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(checkForTouchDevice());
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      setIsContentLoading(true);
      try {
        const response = await fetch('/content.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const liveData: SiteContent = await response.json();
        setLiveContent(liveData);

        if (isAuthenticated && user?.email) {
          setIsEditMode(true);
          try {
            const draftResponse = await fetch('/api/get-draft', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: user.email }),
            });
            if (draftResponse.ok) {
              const draftData = await draftResponse.json();
              setEditableContent(draftData);
            } else {
              setEditableContent(JSON.parse(JSON.stringify(liveData)));
            }
          } catch (draftError) {
            console.warn("Could not load draft, starting with live content:", draftError);
            setEditableContent(JSON.parse(JSON.stringify(liveData)));
          }
        } else {
          setIsEditMode(false);
          setEditableContent(JSON.parse(JSON.stringify(liveData)));
        }
      } catch (error) {
        console.error("Failed to load content:", error);
        setLiveContent(null);
        setEditableContent(null);
      } finally {
        setIsContentLoading(false);
      }
    };

    if (!isLoading) {
      loadContent();
    }
  }, [isAuthenticated, isLoading, user]);
  
  useEffect(() => {
    if (isAuthenticated && !isLoading && window.location.hash === '#admin') {
      setActivePage('admin');
      window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (isEditMode && editableContent && user?.email) {
      const timer = setTimeout(() => {
        fetch('/api/save-draft', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: user.email,
            content: editableContent
          }),
        }).catch(err => console.error("Auto-save failed:", err));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [editableContent, isEditMode, user]);

  const handleSaveContent = async () => {
    if (!editableContent || !user?.email) return;
    setSaveStatus('saving');
    try {
      const response = await fetch('/api/save-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: user.email,
          content: editableContent
        }),
      });
      if (!response.ok) throw new Error('Failed to save content');
      setLiveContent(JSON.parse(JSON.stringify(editableContent)));
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 5000);
    } catch (error) {
      console.error('Save failed:', error);
      setSaveStatus('error');
    }
  };

  const handleDiscardChanges = async () => {
    if (!liveContent || !user?.email) return;
    if (confirm('Are you sure you want to discard all changes? This will revert to the last published version.')) {
      setEditableContent(JSON.parse(JSON.stringify(liveContent)));
      await fetch('/api/delete-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
    }
  };

  const locationConfig: LocationConfig = {
    city: 'Budapest',
    countryCode: 'HU',
  };

  useEffect(() => {
    const lenis = new Lenis();
    function raf(time: number) {
        lenis.raf(time);
        requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }, []);
  
  useEffect(() => {
    const baseTitle = 'Olesya Stepaniuk | Performance-Driven UGC Creator';
    document.title = activePage === 'home' ? baseTitle : `${activePage.charAt(0).toUpperCase() + activePage.slice(1)} | ${baseTitle}`;
  }, [activePage, selectedPost]);
  
  const handleSelectPost = (post: BlogPost) => {
    setSelectedPost(post);
    setActivePage('blog-post');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const contentToDisplay = isEditMode ? editableContent! : liveContent!;

  const renderPage = () => {
    if (activePage === 'admin') {
      if (isAuthenticated && editableContent) {
        return <AdminPage 
          content={editableContent} 
          setContent={setEditableContent} 
          onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          user={user}
          onSave={handleSaveContent}
          onDiscard={handleDiscardChanges}
          saveStatus={saveStatus}
          setActivePage={setActivePage}
        />;
      }
      return (
        <div className="container" style={{ textAlign: 'center', paddingTop: '5rem' }}>
          <h2>Access Denied</h2>
          <p>Please log in to access the admin panel.</p>
        </div>
      );
    }

    switch (activePage) {
      case 'home':
        return <HomePage 
            setActivePage={setActivePage} 
            locationConfig={locationConfig}
            trustedByLogos={contentToDisplay.trustedByLogos}
            featuredWorkDataUGC={contentToDisplay.featuredWorkDataUGC}
            siteSingletonAssets={contentToDisplay.siteSingletonAssets}
        />;
      case 'portfolio':
        return <PortfolioPage 
            setActivePage={setActivePage} 
            caseStudies={contentToDisplay.caseStudiesData}
            photos={contentToDisplay.photosData}
        />;
      case 'about':
        return <AboutPage 
            setActivePage={setActivePage} 
            powerCards={contentToDisplay.powerCardsData}
            keyStats={contentToDisplay.keyStats}
            siteSingletonAssets={contentToDisplay.siteSingletonAssets}
            aboutPhotos={contentToDisplay.aboutPagePhotos}
        />;
      case 'blog':
        return <BlogPage 
            onPostSelect={handleSelectPost} 
            setActivePage={setActivePage} 
            posts={contentToDisplay.blogPosts}
        />;
      case 'blog-post':
        return selectedPost && <BlogPostPage post={selectedPost} onBack={() => setActivePage('blog')} setActivePage={setActivePage} />;
      case 'services':
        return <ServicesPage setActivePage={setActivePage} />;
      case 'contact':
        return <ContactPage setActivePage={setActivePage} siteSingletonAssets={contentToDisplay.siteSingletonAssets} />;
      case 'success':
        return <SuccessPage setActivePage={setActivePage} />;
      default:
        return <HomePage 
            setActivePage={setActivePage} 
            locationConfig={locationConfig}
            trustedByLogos={contentToDisplay.trustedByLogos}
            featuredWorkDataUGC={contentToDisplay.featuredWorkDataUGC}
            siteSingletonAssets={contentToDisplay.siteSingletonAssets}
        />;
    }
  };
  
  if (isContentLoading || isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--background-color)', color: 'var(--heading-color)', fontFamily: 'var(--primary-font)' }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!liveContent || !editableContent) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--background-color)', color: 'var(--heading-color)', fontFamily: 'var(--primary-font)' }}>
        <h2>Error: Could not load site content.</h2>
      </div>
    );
  }

  const hasUnsavedChanges = isEditMode && liveContent && editableContent && JSON.stringify(editableContent) !== JSON.stringify(liveContent);

  return (
    <CursorProvider>
      {!isTouchDevice && <CustomCursor />}
      {activePage !== 'admin' && <Header setActivePage={setActivePage} activePage={activePage} />}
      <main style={{ paddingTop: (activePage === 'admin' || activePage === 'home') ? 0 : '100px' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activePage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Suspense fallback={<PageLoader />}>
              {renderPage()}
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>
      {activePage !== 'admin' && <Footer setActivePage={setActivePage}/>}
      <AnimatePresence>
        {isEditMode && activePage !== 'admin' && (hasUnsavedChanges || saveStatus === 'success') && (
          <EditModeBanner 
            onSave={handleSaveContent}
            onDiscard={handleDiscardChanges}
            status={saveStatus}
          />
        )}
      </AnimatePresence>
    </CursorProvider>
  );
};

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <AuthProvider>
    <App />
  </AuthProvider>
);